// apps/web/src/lib/busca/paleta.ts — Fase 17.8, Plano 15 (D-18, D-25; UI-SPEC U-13, U-14; T-17.8-15-01..04).
//
// Regras puras da paleta Ctrl+K: o termo mínimo, as telas permitidas (busca no cliente), as ações (só navegação), os recentes
// visíveis e a leitura de GET /api/v1/busca. Tudo aqui é ergonomia: a autorização real é do servidor (cada grupo da busca e cada
// rota seguem com o gate deles). Entrada do cookie e da resposta é tratada como não confiável (caminho interno, tamanhos).
import { LIMITE_POR_GRUPO_BUSCA, type BuscaGlobalResponse, type ItemBusca } from '@pieltec/contracts';
import { ARVORE_NAVEGACAO, filtrarNavegacaoPorPermissoes } from '../../components/shell/navegacao';
import { podeAbrirRota } from '../painel/blocos';
import { caminhoValido } from '../prefs-ui';

export const URL_BUSCA = '/api/v1/busca';

/** Teto de telas do cliente e de recentes (UI-SPEC U-13). */
export const MAXIMO_TELAS = 8;
export const MAXIMO_RECENTES = 5;
/** Mesmos limites do servidor (Plano 14): 2 a 60 caracteres, ou 1 dígito. */
const MAXIMO_TERMO = 60;
const CARACTERE_DE_CONTROLE = /\p{Cc}/u;

export interface TelaPaleta {
  readonly id: string;
  readonly rotulo: string;
  readonly caminho: string;
  /** Nome do grupo do menu, usado como apoio na linha. */
  readonly grupo?: string;
}

/** Termo que vale uma busca no servidor: 2 a 60 caracteres ou 1 dígito, após trim, sem caractere de controle. */
export function termoValido(q: string): boolean {
  const termo = q.trim();
  if (termo.length > MAXIMO_TERMO || CARACTERE_DE_CONTROLE.test(termo)) return false;
  return termo.length >= 2 || /^\d$/.test(termo);
}

/** Caixa baixa e sem acento, para comparar "nao conf" com "Não Conformidades". */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('pt-BR');
}

/** Telas do menu que a sessão abre e que casam com o termo (rótulo do item primeiro, depois o nome do grupo); até 8. */
export function telasPermitidas(permissoes: readonly string[], termo: string): readonly TelaPaleta[] {
  const procurado = normalizar(termo.trim());
  if (procurado === '') return [];
  const porRotulo: TelaPaleta[] = [];
  const porGrupo: TelaPaleta[] = [];
  for (const grupo of filtrarNavegacaoPorPermissoes(permissoes)) {
    const candidatas: TelaPaleta[] = grupo.itens?.length
      ? grupo.itens.map((item) => ({ id: item.id, rotulo: item.rotulo, caminho: item.caminho, grupo: grupo.rotulo }))
      : grupo.caminho ? [{ id: grupo.id, rotulo: grupo.rotulo, caminho: grupo.caminho }] : [];
    for (const tela of candidatas) {
      // `podeAbrirRota` é fail-closed: o Painel, por exemplo, só abre com painel:consultar (senão a rota redireciona).
      if (!podeAbrirRota(tela.caminho, permissoes)) continue;
      if (normalizar(tela.rotulo).includes(procurado)) porRotulo.push(tela);
      else if (tela.grupo !== undefined && normalizar(tela.grupo).includes(procurado)) porGrupo.push(tela);
    }
  }
  return [...porRotulo, ...porGrupo].slice(0, MAXIMO_TELAS);
}

const ACOES_DE_NAVEGACAO: readonly { readonly id: string; readonly rotulo: string; readonly caminho: string }[] = [
  { id: 'novo-pv', rotulo: 'Novo PV', caminho: '/vendas/pedidos/novo' },
  { id: 'nova-op', rotulo: 'Preparar produção', caminho: '/producao/nova-op' },
  { id: 'novo-pedido-compra', rotulo: 'Novo pedido de compra', caminho: '/compras/pedidos/novo' },
];

/** Atalhos "Ir para": só abrem a tela de destino (nenhuma mutação) e só aparecem se a sessão abre essa rota. */
export function acoesDeNavegacao(permissoes: readonly string[]): readonly TelaPaleta[] {
  return ACOES_DE_NAVEGACAO.filter((acao) => podeAbrirRota(acao.caminho, permissoes));
}

/** Recentes que a sessão ainda pode abrir (o cookie é adulterável e as permissões mudam), no máximo 5. */
export function recentesVisiveis(recentes: readonly string[], permissoes: readonly string[]): readonly string[] {
  return recentes.filter((caminho) => caminhoValido(caminho) && podeAbrirRota(caminho, permissoes)).slice(0, MAXIMO_RECENTES);
}

const ROTULO_FORA_DO_MENU: Readonly<Record<string, string>> = {
  '/vendas/pedidos/novo': 'Novo PV',
  '/vendas/pedidos/detalhe': 'Detalhe do pedido de venda',
  '/compras/pedidos/novo': 'Novo pedido de compra',
  '/compras/pedidos/detalhe': 'Detalhe do pedido de compra',
};

/** Rótulo de uma página recente: o do menu, um nome para as rotas fora dele, ou o próprio caminho. */
export function rotuloDaRota(caminho: string): string {
  const semQuery = caminho.split(/[?#]/, 1)[0] ?? '';
  for (const grupo of ARVORE_NAVEGACAO) {
    if (grupo.caminho === semQuery) return grupo.rotulo;
    const item = grupo.itens?.find((i) => i.caminho === semQuery);
    if (item) return item.rotulo;
  }
  if (semQuery in ROTULO_FORA_DO_MENU) return ROTULO_FORA_DO_MENU[semQuery] ?? caminho;
  return caminho;
}

export type ResultadoBusca =
  | { readonly tipo: 'sucesso'; readonly dados: BuscaGlobalResponse }
  | { readonly tipo: 'erro' };

const GRUPOS_DA_BUSCA = ['pedidosVenda', 'ordensProducao', 'lotes', 'clientes'] as const;

function itemValido(bruto: unknown): ItemBusca | null {
  if (typeof bruto !== 'object' || bruto === null) return null;
  const { id, titulo, secundario, href } = bruto as Record<string, unknown>;
  if (typeof id !== 'string' || id === '' || typeof titulo !== 'string' || titulo === '') return null;
  if (typeof href !== 'string' || !caminhoValido(href)) return null;
  return typeof secundario === 'string' && secundario !== '' ? { id, titulo, secundario, href } : { id, titulo, href };
}

/** Aceita só grupos que são lista, com itens bem formados e rota interna, até 5 por grupo; o resto é descartado. */
function respostaValida(bruto: unknown): BuscaGlobalResponse | null {
  if (typeof bruto !== 'object' || bruto === null || Array.isArray(bruto)) return null;
  const saida: { -readonly [K in keyof BuscaGlobalResponse]: BuscaGlobalResponse[K] } = {};
  for (const grupo of GRUPOS_DA_BUSCA) {
    const lista = (bruto as Record<string, unknown>)[grupo];
    if (!Array.isArray(lista)) continue;
    saida[grupo] = lista.map(itemValido).filter((i): i is ItemBusca => i !== null).slice(0, LIMITE_POR_GRUPO_BUSCA);
  }
  return saida;
}

/**
 * Lê os registros do servidor. `signal` é cancelado a cada nova digitação. 400, 403, 429, 5xx, rede, cancelamento
 * e corpo ilegível viram `erro`: a paleta mantém as telas e mostra a linha de erro do contrato.
 */
export async function carregarBusca(q: string, signal: AbortSignal, fetchImpl: typeof fetch = fetch): Promise<ResultadoBusca> {
  try {
    const resposta = await fetchImpl(`${URL_BUSCA}?q=${encodeURIComponent(q)}`, { credentials: 'include', signal });
    if (!resposta.ok) return { tipo: 'erro' };
    const dados = respostaValida(await resposta.json());
    return dados === null ? { tipo: 'erro' } : { tipo: 'sucesso', dados };
  } catch {
    return { tipo: 'erro' };
  }
}

/** "?" abre a folha de atalhos, mas nunca enquanto a pessoa digita em campo editável. */
export function deveAbrirFolha(evento: { readonly key: string; readonly alvo: { readonly tagName: string; readonly isContentEditable: boolean } }): boolean {
  if (evento.key !== '?') return false;
  if (evento.alvo.isContentEditable) return false;
  return !['INPUT', 'TEXTAREA', 'SELECT'].includes(evento.alvo.tagName.toUpperCase());
}

/** Plataforma Apple (mostra ⌘ no lugar de Ctrl); recebe o `navigator` para teste. */
export function ehMac(nav: { readonly userAgentData?: { readonly platform?: string }; readonly platform?: string }): boolean {
  const plataforma = nav.userAgentData?.platform ?? nav.platform ?? '';
  return /mac|iphone|ipad/i.test(plataforma);
}
